import React, { useState, useRef, useEffect } from "react";
import { Field } from "formik";
import Popup from "../popup/Popup";
import { POPUP_FORM_CONTENT_CLASS, ACTION_POPUP_CONTENT_INNER_CLASS } from "../popup/popupLayoutSpec";
import { TypeAll } from "../../sections/BusinessDashboard/Settings/Associations/LatestData";
import {
  AccountPopupField,
  AccountPopupStepFooter,
} from "./accountPopupFields";
import {
  AdminDetailFieldLabel,
  AdminMainText,
  AdminMetadataText,
  adminWizardInputClasses,
} from "../../sections/AdminDashboard/shared/adminUx";

const MembershipPopup = ({ isOpen, onClose, setFieldValue, values = {}, errors = {}, initialStep = 0 }) => {
  const [currentStep, setCurrentStep] = useState(initialStep);
  const [searchTerm, setSearchTerm] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedAssociation, setSelectedAssociation] = useState(null);
  const dropdownRef = useRef(null);

  const getAllAssociations = () => {
    const allAssociations = [];
    TypeAll.forEach((category) => {
      category.associationsList.forEach((association) => {
        allAssociations.push({
          name: association.heading,
          image: association.image,
          key: `${association.heading.toLowerCase().replace(/[^a-z0-9]/g, "_")}_url`,
          category: category.heading,
        });
      });
    });
    return allAssociations;
  };

  const allAssociations = getAllAssociations();

  const filteredAssociations = !searchTerm.trim()
    ? allAssociations
    : allAssociations.filter((assoc) =>
        assoc.name.toLowerCase().includes(searchTerm.toLowerCase())
      );

  const handleAssociationSelect = (association) => {
    setSelectedAssociation(association);
    setSearchTerm(association.name);
    setShowDropdown(false);
    setCurrentStep(1);
  };

  const handleClose = () => {
    setSelectedAssociation(null);
    setSearchTerm("");
    setShowDropdown(false);
    setCurrentStep(0);
    onClose();
  };

  const handleNext = () => {
    if (currentStep < 1) setCurrentStep(1);
    else handleClose();
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const urlStep = {
    title: "Membership Profile URL",
    field: selectedAssociation?.key || "url",
    inputType: "url",
    placeholder: "https://example.com/member/your-profile",
  };

  if (!isOpen) return null;

  return (
    <Popup
      setTrigger={() => {}}
      ShowCrossButton={true}
      cross={true}
      onClose={handleClose}
      businessDashboard={true}
      hideLogoInHeader={true}
    >
      <div
        className="overflow-auto overflow-visible relative flex items-center justify-center"
        style={{ minHeight: "calc(100vh - 120px)" }}
      >
        <div className={POPUP_FORM_CONTENT_CLASS}>
          <div className={ACTION_POPUP_CONTENT_INNER_CLASS}>
            {currentStep === 0 ? (
              <div className="relative" ref={dropdownRef}>
                <AdminDetailFieldLabel htmlFor="association-search" className="mb-2 block">
                  Search Association
                </AdminDetailFieldLabel>
                <input
                  id="association-search"
                  type="text"
                  name="search"
                  placeholder="Type to search associations..."
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setShowDropdown(true);
                    setSelectedAssociation(null);
                  }}
                  onFocus={() => setShowDropdown(true)}
                  value={searchTerm}
                  className={adminWizardInputClasses(false)}
                />
                {showDropdown ? (
                  <div className="absolute z-10 w-full mt-1 bg-mistygray border border-lightgray rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {filteredAssociations.length > 0 ? (
                      filteredAssociations.map((association) => (
                        <button
                          key={association.key}
                          type="button"
                          onClick={() => handleAssociationSelect(association)}
                          className="w-full flex items-center p-3 hover:bg-lightgray transition-colors text-left border-b border-lightgray last:border-b-0"
                        >
                          {association.image ? (
                            <img
                              src={association.image}
                              alt={association.name}
                              className="w-8 h-6 object-contain mr-3 flex-shrink-0"
                            />
                          ) : null}
                          <AdminMainText className="text-offblack">{association.name}</AdminMainText>
                        </button>
                      ))
                    ) : (
                      <AdminMetadataText className="p-3 text-center block">
                        No associations found
                      </AdminMetadataText>
                    )}
                  </div>
                ) : null}
              </div>
            ) : (
              <Field name={selectedAssociation?.key}>
                {({ form }) => (
                  <AccountPopupField
                    step={urlStep}
                    fieldKey={selectedAssociation?.key}
                    value={values[selectedAssociation?.key] || ""}
                    onChange={(v) => setFieldValue?.(selectedAssociation?.key, v)}
                    form={{ ...form, errors }}
                  />
                )}
              </Field>
            )}
            <AccountPopupStepFooter
              showBack={currentStep > 0}
              onBack={() => setCurrentStep(0)}
              onNext={handleNext}
              nextLabel={currentStep === 0 ? "Next" : "Save"}
            />
          </div>
        </div>
      </div>
    </Popup>
  );
};

export default MembershipPopup;
